import React from "react";
import { Loader2, Save, Plus, Trash2, Info } from "lucide-react";

const inputCls = "w-full mt-1 px-3 py-2 rounded-lg nb-border bg-[#FFF9F0] focus:outline-none focus:bg-white font-semibold";

const getPath = (obj, path) => path.reduce((o, k) => (o == null ? undefined : o[k]), obj);
const setPath = (obj, path, value) => {
  if (path.length === 0) return value;
  const [head, ...rest] = path;
  const base = obj == null ? (typeof head === "number" ? [] : {}) : Array.isArray(obj) ? [...obj] : { ...obj };
  base[head] = setPath(base[head], rest, value);
  return base;
};

function Section({ title, desc, children, right }) {
  return (
    <div className="bg-white nb-border nb-shadow rounded-2xl p-6">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-head font-extrabold text-xl">{title}</h3>
        {right}
      </div>
      {desc && <p className="text-sm text-nbblack/50 mb-4">{desc}</p>}
      {children}
    </div>
  );
}

export default function AdminPricing({ cfg, setCfg, saving, onSave }) {
  const update = (path, value) => setCfg((c) => setPath(c, path, value));
  const g = (path) => getPath(cfg, path);

  // Reusable numeric field bound to a config path.
  const NF = ({ label, path, step = "0.01", tip, min = "0", cls = "" }) => (
    <div>
      <label className="text-xs font-bold text-nbblack/60 flex items-center gap-1">
        {label}
        {tip && <span title={tip} className="cursor-help text-nbblack/40"><Info size={12} /></span>}
      </label>
      <input
        type="number"
        step={step}
        min={min}
        value={g(path) ?? ""}
        onChange={(e) => update(path, e.target.value)}
        data-testid={`cfg-${path.join("-")}`}
        className={inputCls + " " + cls}
      />
    </div>
  );

  const NumGroup = ({ basePath, fields, cols = 3 }) => (
    <div className={`grid grid-cols-2 md:grid-cols-${cols} gap-4`}>
      {fields.map((f) => (
        <NF key={f.key} label={f.label} path={[...basePath, f.key]} step={f.step || "0.01"} tip={f.tip} />
      ))}
    </div>
  );

  const CUT_FIELDS = [
    { key: "kiss_cut", label: "Kiss cut" },
    { key: "die_cut", label: "Die cut" },
    { key: "sticker_sheet", label: "Sticker sheet" },
    { key: "large_decal", label: "Large decal" },
  ];

  return (
    <div className="space-y-6">
      {/* General */}
      <Section title="General" desc="Product classification thresholds, optional per-unit floor, preview & shipping fields. Prices are CAD, before tax.">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <NF label="Large decal — min dimension (in)" path={["large_decal_min_dim_in"]} step="0.5" tip="Finished width or height greater than this becomes a large decal." />
          <NF label="Large decal — min area (sq in)" path={["large_decal_min_area_sqin"]} step="1" tip="Finished per-sticker area greater than this becomes a large decal." />
          <NF label="Manual quote — max dimension (in)" path={["manual_quote_max_dim_in"]} step="1" tip="Finished dimension greater than this requires a manual quote." />
          <NF label="Default white offset (in)" path={["offset_in"]} step="0.0625" min="0.0625" tip={'Default white border thickness; customers can adjust per design (min 1/16 in).'} />
          <NF label="Sheet nesting gap (in)" path={["sheet_gap_in"]} step="0.05" tip="Legacy preview gap for sheet nesting." />
          <NF label="Production lead time (business days)" path={["lead_time_days"]} step="1" tip="Business days added before ship date." />
        </div>
        <div className="flex items-center gap-2 mt-4">
          <input type="checkbox" checked={!!g(["min_unit_price_enabled"])} onChange={(e) => update(["min_unit_price_enabled"], e.target.checked)} data-testid="cfg-min_unit_price_enabled" className="w-4 h-4" />
          <span className="text-sm font-bold">Enforce a minimum unit price (disabled by default)</span>
          <input type="number" step="0.01" min="0" value={g(["min_unit_price"]) ?? 0} onChange={(e) => update(["min_unit_price"], e.target.value)} className={inputCls + " w-28"} data-testid="cfg-min_unit_price" />
        </div>

        {/* Holidays */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-1">
            <h4 className="font-head font-bold text-sm">Holidays (no shipping)</h4>
            <button onClick={() => setCfg((c) => ({ ...c, holidays: [...(c.holidays || []), ""] }))} className="inline-flex items-center gap-1 font-head font-bold px-3 py-1 rounded-full nb-border nb-press bg-nbcyan text-xs" data-testid="add-holiday">
              <Plus size={14} /> Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {(cfg.holidays || []).map((h, i) => (
              <div key={i} className="flex items-center gap-1">
                <input type="date" value={h} onChange={(e) => update(["holidays", i], e.target.value)} className={inputCls + " w-44"} data-testid={`holiday-${i}`} />
                <button onClick={() => setCfg((c) => ({ ...c, holidays: c.holidays.filter((_, j) => j !== i) }))} className="text-red-600 p-1"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Progressive Area Bands */}
      <Section
        title="Progressive area bands ($/sq ft)"
        desc="Production area is charged progressively band by band (not one flat rate). Leave 'Band size' blank on the final band to price everything beyond as the remainder rate."
        right={<button onClick={() => setCfg((c) => ({ ...c, area_bands: [...c.area_bands, { size_sqft: 1, rate: 5 }] }))} className="inline-flex items-center gap-1 font-head font-bold px-3 py-1.5 rounded-full nb-border nb-press bg-nbcyan text-sm"><Plus size={15} /> Add</button>}
      >
        <div className="space-y-3">
          {(cfg.area_bands || []).map((b, i) => (
            <div key={i} className="flex items-end gap-3" data-testid={`area-band-${i}`}>
              <div className="flex-1">
                <label className="text-xs font-bold text-nbblack/60">Band size (sq ft) — blank = remainder</label>
                <input type="number" step="0.5" value={b.size_sqft ?? ""} placeholder="remainder" onChange={(e) => update(["area_bands", i, "size_sqft"], e.target.value === "" ? null : e.target.value)} className={inputCls} data-testid={`band-size-${i}`} />
              </div>
              <div className="flex-1">
                <label className="text-xs font-bold text-nbblack/60">Selling rate ($/sq ft)</label>
                <input type="number" step="0.01" value={b.rate} onChange={(e) => update(["area_bands", i, "rate"], e.target.value)} className={inputCls} data-testid={`band-rate-${i}`} />
              </div>
              <button onClick={() => setCfg((c) => ({ ...c, area_bands: c.area_bands.filter((_, j) => j !== i) }))} className="text-red-600 p-2"><Trash2 size={18} /></button>
            </div>
          ))}
        </div>
      </Section>

      {/* Materials */}
      <Section title="Materials" desc="Customer surcharge per production sq ft (added on top of the progressive base curve) and internal media cost per sq ft (margin floor only).">
        <div className="space-y-3">
          {(cfg.materials || []).map((m, i) => (
            <div key={m.key} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end border-b border-nbblack/5 pb-3">
              <div>
                <label className="text-xs font-bold text-nbblack/60">Name</label>
                <input value={m.name} onChange={(e) => update(["materials", i, "name"], e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="text-xs font-bold text-nbblack/60">Surcharge ($/sq ft)</label>
                <input type="number" step="0.01" value={m.surcharge_per_sqft} onChange={(e) => update(["materials", i, "surcharge_per_sqft"], e.target.value)} className={inputCls} data-testid={`mat-surcharge-${m.key}`} />
              </div>
              <div>
                <label className="text-xs font-bold text-nbblack/60">Internal cost ($/sq ft)</label>
                <input type="number" step="0.01" value={m.internal_cost_per_sqft} onChange={(e) => update(["materials", i, "internal_cost_per_sqft"], e.target.value)} className={inputCls} data-testid={`mat-cost-${m.key}`} />
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Nesting efficiencies */}
      <Section title="Nesting efficiencies (0–1)" desc="Estimated packing efficiency by shape. Production area = finished area ÷ efficiency.">
        <NumGroup basePath={["nesting_efficiencies"]} cols={3} fields={[
          { key: "contour_cut", label: "Contour cut", step: "0.01" },
          { key: "rectangle", label: "Rectangle / square", step: "0.01" },
          { key: "circle", label: "Circle", step: "0.01" },
        ]} />
      </Section>

      {/* Setup fees + Minimum job prices */}
      <div className="grid md:grid-cols-2 gap-6">
        <Section title="Customer setup fees ($)" desc="One setup fee per job by product type.">
          <NumGroup basePath={["setup_fees"]} cols={2} fields={CUT_FIELDS} />
        </Section>
        <Section title="Minimum job prices ($)" desc="The job total is never lower than this by product type.">
          <NumGroup basePath={["minimum_job_prices"]} cols={2} fields={CUT_FIELDS} />
        </Section>
      </div>

      {/* Cutting rates + handling */}
      <div className="grid md:grid-cols-2 gap-6">
        <Section title="Cut-path rates ($/in)" desc="Customer selling rate per cut inch.">
          <NumGroup basePath={["cut_rates"]} cols={2} fields={[
            { key: "kiss_cut", label: "Kiss cut", step: "0.0001" },
            { key: "die_cut", label: "Die cut", step: "0.0001" },
          ]} />
        </Section>
        <Section title="Handling ($)" desc="Per sticker (kiss/die) or per sheet.">
          <NumGroup basePath={["handling"]} cols={3} fields={[
            { key: "kiss_cut", label: "Kiss cut / sticker" },
            { key: "die_cut", label: "Die cut / sticker" },
            { key: "sticker_sheet", label: "Sticker sheet / sheet" },
          ]} />
        </Section>
      </div>

      {/* Packaging + shipping */}
      <Section
        title="Packaging & shipping reserves"
        desc="First tier whose area AND max-dimension conditions are met wins. Free shipping is customer-facing; the reserve is an internal cost."
        right={<button onClick={() => setCfg((c) => ({ ...c, packaging_shipping_tiers: [...c.packaging_shipping_tiers, { name: "New", max_area_sqft: 5, max_dim_in: 4, packaging: "Box", packaging_charge: 2, shipping_reserve: 5 }] }))} className="inline-flex items-center gap-1 font-head font-bold px-3 py-1.5 rounded-full nb-border nb-press bg-nbcyan text-sm"><Plus size={15} /> Add</button>}
      >
        <div className="space-y-3">
          {(cfg.packaging_shipping_tiers || []).map((tr, i) => (
            <div key={i} className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end border-b border-nbblack/5 pb-3" data-testid={`ship-tier-${i}`}>
              <div><label className="text-[11px] font-bold text-nbblack/60">Name</label><input value={tr.name} onChange={(e) => update(["packaging_shipping_tiers", i, "name"], e.target.value)} className={inputCls} /></div>
              <div><label className="text-[11px] font-bold text-nbblack/60">Max area (sq ft)</label><input type="number" step="0.5" value={tr.max_area_sqft} onChange={(e) => update(["packaging_shipping_tiers", i, "max_area_sqft"], e.target.value)} className={inputCls} /></div>
              <div><label className="text-[11px] font-bold text-nbblack/60">Max dim (in)</label><input type="number" step="0.5" value={tr.max_dim_in} onChange={(e) => update(["packaging_shipping_tiers", i, "max_dim_in"], e.target.value)} className={inputCls} /></div>
              <div><label className="text-[11px] font-bold text-nbblack/60">Packaging</label><input value={tr.packaging} onChange={(e) => update(["packaging_shipping_tiers", i, "packaging"], e.target.value)} className={inputCls} /></div>
              <div><label className="text-[11px] font-bold text-nbblack/60">Pkg charge ($)</label><input type="number" step="0.01" value={tr.packaging_charge} onChange={(e) => update(["packaging_shipping_tiers", i, "packaging_charge"], e.target.value)} className={inputCls} /></div>
              <div className="flex items-end gap-1">
                <div className="flex-1"><label className="text-[11px] font-bold text-nbblack/60">Ship reserve ($)</label><input type="number" step="0.01" value={tr.shipping_reserve} onChange={(e) => update(["packaging_shipping_tiers", i, "shipping_reserve"], e.target.value)} className={inputCls} /></div>
                <button onClick={() => setCfg((c) => ({ ...c, packaging_shipping_tiers: c.packaging_shipping_tiers.filter((_, j) => j !== i) }))} className="text-red-600 p-2"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
        <h4 className="font-head font-bold text-sm mt-5 mb-2">Oversize / high-volume tier</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <NF label="Max dimension (in)" path={["oversize_tier", "max_dim_in"]} step="1" />
          <NF label="Packaging charge ≤18in ($)" path={["oversize_tier", "packaging_charge_18_or_less"]} />
          <NF label="Packaging charge >18in ($)" path={["oversize_tier", "packaging_charge_over_18"]} />
          <NF label="Shipping reserve ($)" path={["oversize_tier", "shipping_reserve"]} />
        </div>
      </Section>

      {/* Internal cost model */}
      <Section title="Internal cost model (margin floor only — never shown to customers)" desc="Direct production costs used to protect gross margin.">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <NF label="Laminate ($/sq ft)" path={["internal_costs", "laminate_per_sqft"]} />
          <NF label="CMYK ink ($/sq ft)" path={["internal_costs", "cmyk_ink_per_sqft"]} />
          <NF label="Machine + labour ($/sq ft)" path={["internal_costs", "production_alloc_per_sqft"]} />
          <NF label="Waste allowance (0–1)" path={["internal_costs", "waste_pct"]} />
        </div>
        <h4 className="font-head font-bold text-sm mb-2">Internal setup cost ($)</h4>
        <NumGroup basePath={["internal_costs", "setup"]} cols={4} fields={CUT_FIELDS} />
        <div className="grid md:grid-cols-2 gap-6 mt-4">
          <div>
            <h4 className="font-head font-bold text-sm mb-2">Internal cut rate ($/in)</h4>
            <NumGroup basePath={["internal_costs", "cut_rates"]} cols={2} fields={[
              { key: "kiss_cut", label: "Kiss cut", step: "0.0001" },
              { key: "die_cut", label: "Die cut", step: "0.0001" },
            ]} />
          </div>
          <div>
            <h4 className="font-head font-bold text-sm mb-2">Internal handling ($)</h4>
            <NumGroup basePath={["internal_costs", "handling"]} cols={3} fields={[
              { key: "kiss_cut", label: "Kiss / sticker" },
              { key: "die_cut", label: "Die / sticker" },
              { key: "sticker_sheet", label: "Sheet / sheet" },
            ]} />
          </div>
        </div>
      </Section>

      {/* Margin protection + payment */}
      <Section title="Margin protection & payment processing" desc="Gross margin target (not markup) plus payment-processing costs used in the margin floor.">
        <NumGroup basePath={[]} cols={3} fields={[]} />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <NF label="Target gross margin (0–1)" path={["target_gross_margin"]} />
          <NF label="Payment processing % (0–1)" path={["payment_processing_pct"]} step="0.001" />
          <NF label="Payment processing fixed ($)" path={["payment_processing_fixed"]} />
        </div>
      </Section>

      {/* Sheet defaults */}
      <Section title="Sticker sheet defaults" desc="Physical sheet + nesting layout used for sheet products.">
        <NumGroup basePath={["sheet"]} cols={4} fields={[
          { key: "width_in", label: "Sheet width (in)", step: "0.5" },
          { key: "height_in", label: "Sheet height (in)", step: "0.5" },
          { key: "margin_in", label: "Margin per side (in)", step: "0.05" },
          { key: "gap_in", label: "Gap between (in)", step: "0.05" },
        ]} />
      </Section>

      {/* Quantity options */}
      <Section
        title="Quantity options"
        desc="Quantities offered in the configurator. Each quote recalculates independently."
        right={<button onClick={() => setCfg((c) => ({ ...c, quantity_options: [...c.quantity_options, { qty: 100, enabled: true, popular: false }] }))} className="inline-flex items-center gap-1 font-head font-bold px-3 py-1.5 rounded-full nb-border nb-press bg-nbcyan text-sm"><Plus size={15} /> Add</button>}
      >
        <div className="flex flex-wrap gap-3">
          {(cfg.quantity_options || []).map((q, i) => (
            <div key={i} className="flex items-center gap-2 bg-[#FFF9F0] nb-border rounded-xl px-3 py-2" data-testid={`qty-opt-${i}`}>
              <input type="number" value={q.qty} onChange={(e) => update(["quantity_options", i, "qty"], e.target.value)} className="w-20 px-2 py-1 rounded nb-border bg-white font-bold" />
              <label className="flex items-center gap-1 text-xs font-bold"><input type="checkbox" checked={!!q.enabled} onChange={(e) => update(["quantity_options", i, "enabled"], e.target.checked)} /> On</label>
              <label className="flex items-center gap-1 text-xs font-bold"><input type="checkbox" checked={!!q.popular} onChange={(e) => update(["quantity_options", i, "popular"], e.target.checked)} /> ★</label>
              <button onClick={() => setCfg((c) => ({ ...c, quantity_options: c.quantity_options.filter((_, j) => j !== i) }))} className="text-red-600"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      </Section>

      {/* Upsell rules */}
      <Section title="Upsell rules" desc="Controls the 'best-value upgrade' recommendation shown to customers.">
        <NumGroup basePath={["upsell"]} cols={5} fields={[
          { key: "min_unit_savings_pct", label: "Min unit savings (%)", step: "0.5" },
          { key: "min_additional_qty", label: "Min extra qty", step: "1" },
          { key: "min_additional_cost", label: "Min extra cost ($)" },
          { key: "max_additional_cost", label: "Max extra cost ($)" },
          { key: "max_qty_multiplier", label: "Max qty multiplier", step: "0.25" },
        ]} />
      </Section>

      {/* Preset sizes */}
      <Section
        title="Preset sizes"
        desc="Quick-pick sizes shown in the designer (width × height in inches)."
        right={<button onClick={() => setCfg((c) => ({ ...c, sizes: [...(c.sizes || []), { label: "New", width_in: 2, height_in: 2 }] }))} className="inline-flex items-center gap-1 font-head font-bold px-3 py-1.5 rounded-full nb-border nb-press bg-nbcyan text-sm"><Plus size={15} /> Add</button>}
      >
        <div className="space-y-3">
          {(cfg.sizes || []).map((s, i) => (
            <div key={i} className="flex items-center gap-3">
              <input value={s.label} onChange={(e) => update(["sizes", i, "label"], e.target.value)} className={inputCls + " flex-1"} placeholder="Label" />
              <input type="number" step="0.5" value={s.width_in} onChange={(e) => update(["sizes", i, "width_in"], e.target.value)} className={inputCls + " w-24"} placeholder="W" />
              <input type="number" step="0.5" value={s.height_in} onChange={(e) => update(["sizes", i, "height_in"], e.target.value)} className={inputCls + " w-24"} placeholder="H" />
              <button onClick={() => setCfg((c) => ({ ...c, sizes: c.sizes.filter((_, j) => j !== i) }))} className="text-red-600 p-2"><Trash2 size={18} /></button>
            </div>
          ))}
        </div>
      </Section>

      <button
        data-testid="save-pricing"
        onClick={onSave}
        disabled={saving}
        className="inline-flex items-center gap-2 bg-nbblack text-white font-head font-bold text-lg px-8 py-3 rounded-full nb-border nb-shadow nb-press disabled:opacity-60"
      >
        {saving ? <Loader2 className="animate-spin" size={20} /> : <Save size={20} />} Save pricing
      </button>
    </div>
  );
}
